import { Alert, Flex, Modal, Skeleton, Typography } from "antd"
import { useEffect, useMemo, useState } from "react"
import VSpace from "../../common/VSpace"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { StatementSummary } from "../../../domain/invoicing/statement"
import { useSendStatementMutation } from "../../../domain/invoicing/statementsQueries"
import { useInvoicingSettingsQuery } from "../../../domain/invoicing/settings/invoicingSettingsQueries"
import { defaultInvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import { useCustomerEmailsQuery } from "../../../domain/customers/customerQueries"
import { getBestCustomerEmail } from "../../../domain/customers/customer"
import { getDefaultDueDate } from "../Creation/createInvoiceModel"
import PaymentMethodsCard from "./PaymentMethodsCard"
import PaymentMethodOnFileCard from "./PaymentMethodOnFileCard"
import DueDateCard from "./DueDateCard"
import { initializeSendStatementRequest } from "./sendStatementModel"

const { Text } = Typography

type Props = {
  onClose: () => void
  statement: StatementSummary
}

const SendStatementModal = ({ onClose, statement }: Props) => {
  const customerEmailsQuery = useCustomerEmailsQuery(statement.customerId)
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const sendMutation = useSendStatementMutation()

  const invoicingSettings = invoicingSettingsQuery.data ?? defaultInvoicingSettings()
  const bestEmail = getBestCustomerEmail(customerEmailsQuery.data ?? [])
  const defaultDueOn = useMemo(() => getDefaultDueDate(invoicingSettings), [invoicingSettings.defaultDueInDays])

  const [request, setRequest] = useState(initializeSendStatementRequest(statement, [], defaultDueOn))

  useEffect(() => {
    setRequest(initializeSendStatementRequest(statement, bestEmail ? [bestEmail] : [], defaultDueOn))
  }, [statement.statementId, bestEmail, defaultDueOn])

  const isLoading = customerEmailsQuery.isLoading || invoicingSettingsQuery.isLoading

  return <Modal
    open
    onCancel={onClose}
    onOk={() => sendMutation.mutate(request, { onSuccess: onClose })}
    title="Send Statement"
    okText="Send"
    okButtonProps={{ loading: sendMutation.isLoading, disabled: sendMutation.isLoading || !bestEmail }}
    width={800}
  >
    {isLoading && <Skeleton active />}
    {!isLoading && <VSpace>
      {bestEmail
        ? <Text>Sending to <strong>{bestEmail}</strong></Text>
        : <Alert type="warning" message="No email on file for this customer. Add one to send the statement." />}
      <PaymentMethodsCard
        options={request.invoiceUpdates}
        onUpdate={(invoiceUpdates) => setRequest({ ...request, invoiceUpdates })}
        documentLabel="Statement"
      />
      <PaymentMethodOnFileCard
        options={request.invoiceUpdates}
        onUpdate={(invoiceUpdates) => setRequest({ ...request, invoiceUpdates })}
      />
      <DueDateCard
        options={request.invoiceUpdates}
        onUpdate={(invoiceUpdates) => setRequest({ ...request, invoiceUpdates })}
        defaultDueDate={defaultDueOn}
        documentLabel="Statement"
      />
      <div style={{ borderTop: '1px solid #00000026', borderBottom: '1px solid #00000026', padding: '12px 0', fontSize: '16px' }}>
        <Flex justify="space-between" gap={8}>
          <strong>Statement Total</strong>
          <strong>{centsToDollarsDisplay(statement.total)}</strong>
        </Flex>
      </div>
    </VSpace>}
  </Modal>
}

export default SendStatementModal
